/**
 * The native app's boot cache: the last `/dashboard/shell` and `/dashboard/home` payloads this device loaded, for
 * the account signed in right now, so the next launch paints the workspace at once (useDashboardBootstrap's
 * optimistic boot) and offline shows what the creator had. A module of its own so sign-out (LogOut.vue, App.vue)
 * can clear it without pulling the dashboard stores into the app's entry chunk.
 */
import { readPersistedLocalSessionRaw } from '@/utils/supabaseLocalSession'
import { isNativePlatform } from '@/utils/platform'

export const BOOT_CACHE_KEY = 'lh:boot-cache:v1'
/** Older than this, a cached workspace is more confusing than a skeleton. */
const BOOT_CACHE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

export interface BootCache {
    userId: string
    savedAt: number
    shell?: Record<string, unknown>
    home?: Record<string, unknown>
}

/** The signed-in user id from the persisted Supabase session (synchronous: usable before auth is ready). */
function persistedSessionUserId(): string | null {
    try {
        const raw = readPersistedLocalSessionRaw()
        if (!raw) return null
        const parsed = JSON.parse(raw) as { user?: { id?: string } }
        return parsed?.user?.id ?? null
    } catch {
        return null
    }
}

function bootCacheEnabled(): boolean {
    return isNativePlatform()
}

export function readBootCache(): BootCache | null {
    if (!bootCacheEnabled()) return null
    try {
        const raw = localStorage.getItem(BOOT_CACHE_KEY)
        if (!raw) return null
        const cache = JSON.parse(raw) as BootCache
        const sessionUser = persistedSessionUserId()
        // Only ever the account that is signed in on this device right now.
        if (!cache?.userId || cache.userId !== sessionUser) return null
        if (Date.now() - (cache.savedAt || 0) > BOOT_CACHE_MAX_AGE_MS) return null
        return cache
    } catch {
        return null
    }
}

export function writeBootCache(part: 'shell' | 'home', userId: string | undefined, data: Record<string, unknown>) {
    if (!bootCacheEnabled() || !userId) return
    try {
        const prev = readBootCache()
        const next: BootCache = {
            ...(prev && prev.userId === userId ? prev : {}),
            userId,
            savedAt: Date.now(),
            [part]: data,
        }
        localStorage.setItem(BOOT_CACHE_KEY, JSON.stringify(next))
    } catch {
        // Quota or blocked storage: the next launch just loads normally.
    }
}

/** Sign-out: nothing of this account stays on the device. */
export function clearBootCache() {
    try {
        localStorage.removeItem(BOOT_CACHE_KEY)
    } catch {
        // ignore
    }
}
